// Custom hook for WSR actions that integrates with centralized navbar
import { useState, useEffect, useCallback } from 'react';
import WSRUploadService from '../services/wsrUploadService';
import WSRExportService from '../services/wsrExportService';
import { useToast } from '../../../components/Toast';

interface WSRData {
  id?: number;
  client_id: number;
  week_ending: string;
  reporting_period: string;
  reporting_date: string;
  project_sponsor: string;
  prepared_by: string;
  phone: string;
  email: string;
  report_summary: string;
  conclusions_recommendations: string;
  status: string;
  accomplished: any[];
  targets: any[];
  risks: any[];
  metrics: any[];
}

interface UseWSRActionsProps {
  currentWSR?: WSRData;
  onWSRUpdate?: (wsr: WSRData) => void;
}

export const useWSRActions = ({ currentWSR, onWSRUpdate }: UseWSRActionsProps = {}) => {
  const [uploadedAnalysis, setUploadedAnalysis] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { addToast } = useToast();

  const uploadService = WSRUploadService.getInstance();
  const exportService = WSRExportService.getInstance();

  // Register for upload analysis callbacks
  useEffect(() => {
    const handleAnalysis = (analysis: any) => {
      setUploadedAnalysis(analysis);
      addToast('📋 WSR file analyzed and ready to use', 'success');
    };

    uploadService.onAnalysisComplete(handleAnalysis);

    return () => {
      uploadService.removeAnalysisCallback(handleAnalysis);
    };
  }, [uploadService, addToast]);

  // Handle file upload from navbar
  const handleUpload = useCallback(async (file: File) => {
    setIsProcessing(true);
    try {
      await uploadService.handleFileUpload(file);
      addToast('✅ WSR file uploaded and analyzed successfully', 'success');
    } catch (error) {
      console.error('Upload error:', error);
      addToast(`❌ Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [uploadService, addToast]);

  // Handle export actions from navbar
  const handleExport = useCallback(async (format: 'markdown' | 'pdf' | 'json' | 'email') => {
    if (!currentWSR) {
      addToast('❌ No WSR data to export', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      switch (format) {
        case 'markdown':
          await exportService.exportAsMarkdown(currentWSR);
          addToast('📄 WSR exported as Markdown', 'success');
          break;
        case 'pdf':
          await exportService.exportAsPDF();
          addToast('📄 Opening print dialog for PDF export', 'info');
          break;
        case 'json':
          await exportService.exportAsJSON(currentWSR);
          addToast('📄 WSR exported as JSON', 'success');
          break;
        case 'email':
          await exportService.emailWSR(currentWSR);
          addToast('📧 Opening email client with WSR content', 'info');
          break;
      }
    } catch (error) {
      console.error('Export error:', error);
      addToast(`❌ Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [currentWSR, exportService, addToast]);

  // Handle share action from navbar
  const handleShare = useCallback(async () => {
    if (!currentWSR?.id) {
      addToast('❌ WSR must be saved before sharing', 'error');
      return;
    }

    try {
      const shareLink = exportService.generateShareableLink(currentWSR.id);
      await navigator.clipboard.writeText(shareLink);
      addToast('🔗 Share link copied to clipboard', 'success');
    } catch (error) {
      console.error('Share error:', error);
      addToast('❌ Failed to copy share link', 'error');
    }
  }, [currentWSR, exportService, addToast]);

  // Handle print action from navbar
  const handlePrint = useCallback(() => {
    window.print();
    addToast('🖨️ Opening print dialog', 'info');
  }, [addToast]);

  // Handle search action from navbar
  const handleSearch = useCallback((query: string) => {
    // This would integrate with your search functionality
    console.log('WSR Search:', query);
    addToast(`🔍 Searching WSRs for: "${query}"`, 'info');
  }, [addToast]);

  // Clear uploaded analysis
  const clearUploadedAnalysis = useCallback(() => {
    setUploadedAnalysis(null);
  }, []);

  return {
    // State
    uploadedAnalysis,
    isProcessing,
    
    // Actions
    handleUpload,
    handleExport,
    handleShare,
    handlePrint,
    handleSearch,
    clearUploadedAnalysis,
    
    // Services (for direct access if needed)
    uploadService,
    exportService
  };
};

export default useWSRActions;